import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { MeasureTableModel, MeasureTableMeasureModel } from '../../shared/model/measure-table.model';
import { MeasureTableService } from '../../shared/service/measure-table.service';
import { ProductService } from '../../shared/service/product.service';
import { MessageComponent } from '../../shared/component/message/message.component';

@Component({
    selector: 'app-measure-table-form',
    templateUrl: './measure-table-form.component.html',
    styleUrls: ['./measure-table-form.component.css']
})
export class MeasureTableFormComponent implements OnInit {

    @ViewChild(MessageComponent) message: MessageComponent;

    measureTable: MeasureTableModel = new MeasureTableModel();
    editMode: boolean = false;
    isLoading: boolean = false;
    newLabel: string = '';

    // ── Propagação ───────────────────────────────────────────────────────
    propagateTargetIds: any[] = [];
    allTablesOptions: any[] = [];
    loadingPropagateOptions: boolean = false;

    // ── Produtos vinculados ──────────────────────────────────────────────
    linkedProducts: any[] = [];
    linkedProductsLoading: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private measureTableService: MeasureTableService,
        private productService: ProductService
    ) { }

    ngOnInit() {
        const id = this.route.snapshot.paramMap.get('id');
        if (id && id !== 'new') {
            this.editMode = true;
            this.loadMeasureTable(Number(id));
            this.loadLinkedProducts(Number(id));
        } else {
            this.addMeasure();
        }
    }

    loadMeasureTable(id: number) {
        this.isLoading = true;
        this.measureTableService.getById(id).subscribe(
            (result: any) => {
                this.measureTable = Object.assign(new MeasureTableModel(), result);
                this.isLoading = false;
                this.loadPropagateOptions(id);
            },
            () => {
                this.message.error();
                this.isLoading = false;
            }
        );
    }

    loadPropagateOptions(currentId: number) {
        this.loadingPropagateOptions = true;
        this.measureTableService.getPaginated(0, 1e8).subscribe(
            (result: any) => {
                const rows = result['rows'] || [];
                this.allTablesOptions = rows
                    .filter((t: any) => (t.id || t.measure_table_id) !== currentId)
                    .map((t: any) => ({
                        label: `${t.name} — ${t.model_code}`,
                        value: t.id || t.measure_table_id
                    }));
                this.loadingPropagateOptions = false;
            },
            () => { this.loadingPropagateOptions = false; }
        );
    }

    loadLinkedProducts(measureTableId: number) {
        this.linkedProductsLoading = true;
        this.productService.getPaginated(0, 1000, undefined, measureTableId).subscribe(
            (result: any) => {
                this.linkedProducts = result['rows'] || [];
                this.linkedProductsLoading = false;
            },
            () => { this.linkedProductsLoading = false; }
        );
    }

    // ── Seção B: colunas do grid ──────────────────────────────────────────

    addColumn() {
        const label = this.newLabel.trim();
        if (!label) return;
        this.measureTable.grid_labels = [...this.measureTable.grid_labels, label];
        this.measureTable.measures.forEach(m => m.values.push(null));
        this.newLabel = '';
    }

    removeColumn(index: number) {
        const labels = [...this.measureTable.grid_labels];
        labels.splice(index, 1);
        this.measureTable.grid_labels = labels;
        this.measureTable.measures.forEach(m => m.values.splice(index, 1));
    }

    moveColumnLeft(index: number) {
        if (index === 0) return;
        this.swapColumns(index, index - 1);
    }

    moveColumnRight(index: number) {
        if (index >= this.measureTable.grid_labels.length - 1) return;
        this.swapColumns(index, index + 1);
    }

    private swapColumns(i: number, j: number) {
        const labels = [...this.measureTable.grid_labels];
        [labels[i], labels[j]] = [labels[j], labels[i]];
        this.measureTable.grid_labels = labels;
        this.measureTable.measures.forEach(m => {
            [m.values[i], m.values[j]] = [m.values[j], m.values[i]];
        });
    }

    onLabelKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.addColumn();
        }
    }

    // ── Seção C: linhas de medida ─────────────────────────────────────────

    addMeasure() {
        const colCount = this.measureTable.grid_labels.length;
        const measure = new MeasureTableMeasureModel(colCount);
        measure.sequence = this.measureTable.measures.length + 1;
        this.measureTable.measures.push(measure);
    }

    removeMeasure(index: number) {
        this.measureTable.measures.splice(index, 1);
        this.updateSequences();
    }

    moveMeasureUp(index: number) {
        if (index === 0) return;
        const measures = this.measureTable.measures;
        [measures[index - 1], measures[index]] = [measures[index], measures[index - 1]];
        this.updateSequences();
    }

    moveMeasureDown(index: number) {
        if (index >= this.measureTable.measures.length - 1) return;
        const measures = this.measureTable.measures;
        [measures[index], measures[index + 1]] = [measures[index + 1], measures[index]];
        this.updateSequences();
    }

    private updateSequences() {
        this.measureTable.measures.forEach((m, i) => m.sequence = i + 1);
    }

    // ── Navegação por Tab/Enter na matriz ─────────────────────────────────

    onCellKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number) {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            const totalCols = this.measureTable.grid_labels.length + 1; // +1 = tolerância
            const nextCol = colIndex + 1;
            if (nextCol < totalCols) {
                this.focusCell(rowIndex, nextCol);
            } else {
                if (rowIndex + 1 >= this.measureTable.measures.length) {
                    this.addMeasure();
                    setTimeout(() => this.focusCell(rowIndex + 1, 0), 50);
                } else {
                    this.focusCell(rowIndex + 1, 0);
                }
            }
        }
    }

    private focusCell(row: number, col: number) {
        const selector = `[data-cell="${row}-${col}"]`;
        const el = document.querySelector(selector) as HTMLInputElement;
        if (el) el.focus();
    }

    // ── Colar de planilha (Excel paste) ───────────────────────────────────

    onMatrixPaste(event: ClipboardEvent) {
        event.preventDefault();
        const text = event.clipboardData.getData('text');
        if (!text) return;

        const rows = text.split('\n').map(r => r.replace(/\r$/, '')).filter(r => r.trim());
        if (rows.length === 0) return;

        // detectar se a primeira linha é cabeçalho (não numérica nas colunas de valor)
        let dataRows = rows;
        const firstCells = rows[0].split('\t');
        const hasHeader = firstCells.length > 2 && isNaN(Number(firstCells[2]));
        if (hasHeader) {
            const labelCells = firstCells.slice(2); // pula Medida e Tolerância
            this.measureTable.grid_labels = labelCells.map(l => l.trim()).filter(l => l);
            dataRows = rows.slice(1);
        }

        const newMeasures: MeasureTableMeasureModel[] = [];
        dataRows.forEach((row, ri) => {
            const cells = row.split('\t');
            const measure = new MeasureTableMeasureModel();
            measure.sequence = ri + 1;
            measure.description = cells[0] ? cells[0].trim() : '';
            measure.tolerance = cells[1] ? Number(cells[1].replace(',', '.')) : 0;
            measure.values = this.measureTable.grid_labels.map((_, ci) => {
                const raw = cells[ci + 2] ? cells[ci + 2].trim().replace(',', '.') : null;
                return raw !== null && raw !== '' ? Number(raw) : null;
            });
            newMeasures.push(measure);
        });

        this.measureTable.measures = newMeasures;
    }

    // ── Validação e salvamento ────────────────────────────────────────────

    validate(): boolean {
        return MeasureTableModel.validate(this.measureTable);
    }

    blockComma(event: KeyboardEvent) {
        if (event.key === ',') event.preventDefault();
    }

    private preparePayload() {
        this.updateSequences();
        this.measureTable.measures.forEach(m => {
            m.values = m.values.map(v =>
                v !== null && v !== undefined && v !== '' ? Number(v) : null
            );
        });
    }

    onSave() {
        if (!this.validate()) {
            this.message.error('need_more_data');
            return;
        }
        this.preparePayload();
        this.measureTableService.save(this.measureTable).subscribe(
            () => {
                if (this.propagateTargetIds.length > 0) {
                    this.propagateToTargets();
                } else {
                    this.message.success('success_save_message');
                    setTimeout(() => this.router.navigate(['/measure-table']), 1500);
                }
            },
            () => this.message.error()
        );
    }

    private propagateToTargets() {
        const total = this.propagateTargetIds.length;
        let completed = 0;
        let hasError = false;

        this.propagateTargetIds.forEach(targetId => {
            this.measureTableService.getById(targetId).subscribe(
                (target: any) => {
                    const updated = Object.assign({}, target, {
                        grid_labels: this.measureTable.grid_labels,
                        measures: this.measureTable.measures
                    });
                    this.measureTableService.put(updated).subscribe(
                        () => {
                            completed++;
                            if (completed === total && !hasError) {
                                this.message.success('success_save_message');
                                setTimeout(() => this.router.navigate(['/measure-table']), 1500);
                            }
                        },
                        () => { hasError = true; this.message.error(); }
                    );
                },
                () => { hasError = true; this.message.error(); }
            );
        });
    }

    onSaveAndNew() {
        if (!this.validate()) {
            this.message.error('need_more_data');
            return;
        }
        this.preparePayload();
        this.measureTableService.save(this.measureTable).subscribe(
            () => {
                this.message.success('success_save_message');
                this.resetForm();
            },
            () => this.message.error()
        );
    }

    private resetForm() {
        this.measureTable = new MeasureTableModel();
        this.editMode = false;
        this.newLabel = '';
        this.addMeasure();
    }

    onCancel() {
        this.router.navigate(['/measure-table']);
    }

    // ── Getters utilitários ───────────────────────────────────────────────

    get summaryText(): string {
        const rows = this.measureTable.measures.length;
        const cols = this.measureTable.grid_labels.length;
        return `${rows} medidas × ${cols} colunas`;
    }

    get hasUniqueLabels(): boolean {
        const labels = this.measureTable.grid_labels.map(l => l ? l.trim() : '');
        return new Set(labels).size === labels.length;
    }

}



// WEBPACK FOOTER //
// C:/Users/Emanuel/Documents/EasyCheck/easy-check-front-web/src/app/measure-table/measure-table-form/measure-table-form.component.ts